import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { cn } from "../../lib/utils";

/**
 * shadcn/ui Select — the standard component, ported to this codebase.
 *
 * It is a thin, pre-styled wrapper over @radix-ui/react-select (the same
 * engine the shadcn CLI would install). Differences from the upstream file are
 * only cosmetic to fit this repo: JSX instead of TSX, relative imports instead
 * of the `@/` alias, heroicons instead of lucide, and the project's Tailwind
 * tokens (gray-275 / orange-150 / black-150) so it matches the pre-call design.
 *
 * Radix handles keyboard navigation, typeahead, outside-click/Escape close, and
 * collision-aware positioning (via a portal) — so consumers don't reimplement
 * any of that.
 */

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef(function SelectTrigger(
  { className, children, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex w-full items-center gap-1.5 rounded-lg border bg-white px-2 py-2 text-left sm:gap-2 sm:px-3",
        "border-gray-275 transition-colors hover:border-gray-400",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-150/60",
        "data-[state=open]:border-orange-150",
        "data-[placeholder]:text-gray-150",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronUpDownIcon
          className="h-4 w-4 shrink-0 text-gray-150 sm:h-5 sm:w-5"
          aria-hidden="true"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef(function SelectContent(
  { className, children, footer, position = "popper", sideOffset = 8, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-72 max-w-[85vw] overflow-hidden rounded-xl border border-gray-275 bg-white shadow-xl",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport className="max-h-56 overflow-y-auto py-1">
          {children}
        </SelectPrimitive.Viewport>
        {footer && (
          <div
            className="border-t border-gray-275 p-2"
            // The footer holds non-item, interactive content (a live meter, a
            // "Test Speakers" button). Radix's listbox owns pointer/keyboard
            // events for item navigation, typeahead and auto-close — stop them
            // at the footer so a click here reaches the button instead of being
            // treated as a selection (and so typing doesn't trigger typeahead).
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {footer}
          </div>
        )}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef(function SelectItem(
  { className, children, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center gap-3 px-4 py-2.5 text-sm text-black-150 outline-none",
        "data-[highlighted]:bg-gray-100 data-[state=checked]:font-semibold",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </span>
      <SelectPrimitive.ItemIndicator asChild>
        <CheckCircleIcon
          className="h-5 w-5 shrink-0 text-gray-400"
          aria-hidden="true"
        />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem };
